import React, { useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import Table from '../../components/Table'
import { getReportsRequested, addReportRequested, deleteReportRequested } from "../../actions/reportAction";
import { connect } from "react-redux";
import { ChartCard, AttendanceTrendChart } from "../../components/Charts";
import { TrashIcon, PlusCircleIcon, ClipboardListIcon, ChartBarIcon, UserGroupIcon } from '@heroicons/react/outline';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import moment from 'moment';

const Report = ({
    reportsGetRequested,
    reports,
    getReportsRequested: getReport,
    deleteReportRequested: deleteReport,
}) => {
    const toNumber = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const navigate = useNavigate();
    const { activeCell } = useOutletContext() || {};
    const cellId = activeCell?.id;

    // Refetch whenever the selected cell changes.
    useEffect(() => {
        if (cellId) {
            getReport(cellId);
        }
    }, [cellId]);

    const handleCreateReport = () => {
        navigate(`/dashboard/reports/create/${cellId}`, { state: { cellId, data: {} } });
    }

    const notify = (msg) => toast.success(msg, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
        theme: "light",
    });

    const handleDeleteReport = (data_id) => {
        console.log("Delete Report");
        const confirmDelete = window.confirm("Are you sure you want to delete this report?");
        if (!confirmDelete) return;
        notify("Deleting report...");
        deleteReport({ cellId, id: data_id });
    };

    const columns = React.useMemo(() => [
        {
            Header: "#",
            Cell: ({ cell }) => (
                <div className="flex items-center space-x-4">
                    <p>{cell.row.index + 1}</p>
                </div>
            ),
        },
        {
            Header: "Date",
            accessor: 'date',
            Cell: ({ cell }) => (
                <div className="text-sm text-gray-500">
                    {moment(cell.row.original.date, 'YYYY-MM-DD').format('YYYY-MM-DD')}
                </div>
            ),
        },
        {
            Header: "Total Members",
            accessor: 'cellTotal',
        },
        {
            Header: "Total Present",
            accessor: 'attendanceTotal',
        },
        {
            Header: "No. Guests",
            accessor: 'guestsTotal',
        },
        {
            Header: "Actions",
            accessor: 'status',
            idAccessor: 'id',
            // Cell: ActionsPill,
            Cell: ({ cell, row, column }) => (
                <div className="flex items-center space-x-1">
                    <button
                        className="px-3 py-1 uppercase leading-wide font-bold text-xs rounded-full shadow-sm bg-yellow-100 text-yellow-800"
                        onClick={() => navigate(`/dashboard/reports/edit/${cellId}/${cell.row.original.id}`)}
                    >
                        Edit
                    </button>
                    <button
                        className="px-3 py-1 uppercase leading-wide font-bold text-xs rounded-full shadow-sm bg-red-100 text-red-800"
                        onClick={() => handleDeleteReport(row.original[column.idAccessor])}
                    >
                        <TrashIcon className="h-5 w-5" />
                    </button>
                </div>
            ),
        },
    ], [])

    const sortedReports = React.useMemo(() => (
        [...(reports || [])].sort((a, b) =>
            moment(b.date, 'YYYY-MM-DD').valueOf() - moment(a.date, 'YYYY-MM-DD').valueOf()
        )
    ), [reports]);

    const totalReports = reports?.length || 0;
    const totalAttendance = (reports || []).reduce((sum, reportItem) => (
        sum + toNumber(reportItem.attendanceTotal)
    ), 0);
    const totalGuests = (reports || []).reduce((sum, reportItem) => (
        sum + toNumber(reportItem.guestsTotal)
    ), 0);
    const attendanceHistoryData = [...(reports || [])]
        .filter((reportItem) => reportItem?.date)
        .sort((a, b) => moment(a.date, 'YYYY-MM-DD').valueOf() - moment(b.date, 'YYYY-MM-DD').valueOf())
        .slice(-12)
        .map((reportItem) => ({
            week: moment(reportItem.date, 'YYYY-MM-DD').format('MMM D'),
            totalMembers: toNumber(reportItem.cellTotal),
            attendance: toNumber(reportItem.attendanceTotal),
        }));

    return (
        <div className="min-h-screen bg-gray-100 text-gray-900">
            <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Cell Reports</h1>
                        <p className="text-gray-500 mt-1">Capture attendance, testimonies, and evaluations each week.</p>
                    </div>
                    {cellId && (
                        <button
                            onClick={handleCreateReport}
                            className="flex flex-row items-center gap-2 bg-emerald-500 text-white hover:bg-emerald-600 active:bg-emerald-700 font-semibold px-4 py-2 rounded-lg shadow-md transition-all duration-150"
                            type="button"
                        >
                            <PlusCircleIcon className="h-5 w-5" />
                            Create Report
                        </button>
                    )}
                </div>

                {cellId === undefined ? (
                    <div className="bg-white shadow-lg rounded-lg p-8 flex flex-col items-center justify-center">
                        <UserGroupIcon className="h-16 w-16 text-gray-300 mb-4" />
                        <h2 className="text-xl font-semibold text-gray-700">No assigned cell</h2>
                        <p className="text-gray-500 mt-1">This account is not assigned to a cell yet. Please contact an admin.</p>
                    </div>
                ) : (
                    <>
                        {totalReports > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <StatCard title="Reports Submitted" value={totalReports} icon={<ClipboardListIcon className="h-6 w-6 text-white" />} bgColor="bg-emerald-500" />
                                <StatCard title="Total Attendance" value={totalAttendance} icon={<ChartBarIcon className="h-6 w-6 text-white" />} bgColor="bg-blue-500" />
                                <StatCard title="Guests Recorded" value={totalGuests} icon={<UserGroupIcon className="h-6 w-6 text-white" />} bgColor="bg-indigo-500" />
                            </div>
                        )}

                        {totalReports > 0 && (
                            <div className="mb-6">
                                <ChartCard title="Attendance History">
                                    <AttendanceTrendChart data={attendanceHistoryData} showCounts />
                                </ChartCard>
                            </div>
                        )}

                        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                            {reports && reports.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <Table columns={columns} data={sortedReports} />
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                                    <ClipboardListIcon className="h-16 w-16 text-gray-300 mb-4" />
                                    <h2 className="text-xl font-semibold text-gray-700">No reports found</h2>
                                    <p className="text-gray-500 mt-1">Create your first report to start tracking cell growth and attendance.</p>
                                    <button
                                        onClick={handleCreateReport}
                                        className="mt-4 flex items-center gap-2 bg-emerald-500 text-white hover:bg-emerald-600 font-medium px-4 py-2 rounded-lg transition-colors"
                                        type="button"
                                    >
                                        <PlusCircleIcon className="h-5 w-5" />
                                        Create First Report
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}

const StatCard = ({ title, value, icon, bgColor }) => (
    <div className="bg-white shadow-lg rounded-lg p-6">
        <div className="flex items-center justify-between">
            <div>
                <p className="text-gray-600 text-sm font-medium mb-1">{title}</p>
                <p className="text-3xl font-bold text-gray-900">{value}</p>
            </div>
            <div className={`${bgColor} rounded-full p-3`}>{icon}</div>
        </div>
    </div>
);


Report.propTypes = {
    reports: PropTypes.array,
    reportsGetRequested: PropTypes.bool,
    getReportsRequested: PropTypes.func,
    addReportRequested: PropTypes.func,
};

Report.defaultProps = {
    reports: [],
    reportsGetRequested: false,
    reportGetRequested: false,
};

const mapStateToProps = (state) => ({
    reports: state.report.reports,
    reportsGetRequested: state.report.reportsGetRequested,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
    getReportsRequested,
    addReportRequested,
    deleteReportRequested,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Report);
